"use client";

/**
 * ConditionalAppShell — decides shell-vs-bare by route, ported from V1's
 * web/components/layout/ConditionalLayout.tsx.
 *
 * BARE (children only, no chrome):
 *   - public / marketing / auth: /, /home, /demo, /login, /register, /auth/...,
 *     /terms, /privacy, /refund, /v/... (share links)
 *   - full-screen editor/studio (must stay chromeless): any path that
 *     startsWith("/editor"), or endsWith("/studio"), or endsWith("/write")
 *   - /pricing: bare when logged-out, shell when logged-in (auth-aware).
 *     While session is loading, render bare to avoid a layout flash.
 *
 * SHELL (<AppShell>{children}</AppShell>):
 *   - everything else — the authed management pages (/dashboard, /generate,
 *     /projects, /projects/[id], /projects/[id]/episodes/[ep_id] detail,
 *     /productions, /credits, /account, /profile, /settings, /admin/*).
 *
 * Note the studio nuance: /projects/[id]/episodes/[ep_id]/studio is BARE
 * (full-screen editor) while /projects/[id]/episodes/[ep_id] (episode detail)
 * gets the shell — the endsWith("/studio") rule handles that split.
 */

import { usePathname } from "next/navigation";
import { useSession } from "@/lib/auth/use-session";
import { useAuthHint } from "@/lib/auth/auth-hint";
import { AppShell } from "./app-shell";

// Exact-match routes that are ALWAYS bare regardless of auth state.
const ALWAYS_BARE_EXACT = new Set<string>([
	"/",
	"/home",
	"/demo",
	"/login",
	"/register",
]);

// Prefixes that are ALWAYS bare.
const ALWAYS_BARE_PREFIX = ["/auth/", "/v/", "/editor"];

// Bare for logged-out visitors, shell for logged-in users. The legal/marketing
// pages join /pricing here so a logged-in user gets the app chrome (sidebar +
// user button + a way back) instead of being stranded on a bare page. Each of
// these pages hides its OWN marketing header via <HideWhenAuthed> so the shell
// doesn't double up.
const AUTH_AWARE_EXACT = new Set<string>([
	"/pricing",
	"/terms",
	"/privacy",
	"/refund",
]);

function isAlwaysBare(pathname: string): boolean {
	if (ALWAYS_BARE_EXACT.has(pathname)) return true;
	// Share-link root "/v" and auth root "/auth" (without trailing segment).
	if (pathname === "/v" || pathname === "/auth") return true;
	if (ALWAYS_BARE_PREFIX.some((p) => pathname.startsWith(p))) return true;
	// Full-screen editor surfaces — keep chromeless wherever they live.
	if (pathname.endsWith("/studio") || pathname.endsWith("/write")) return true;
	return false;
}

export function ConditionalAppShell({
	children,
}: {
	children: React.ReactNode;
}) {
	const pathname = usePathname();
	const { user, status } = useSession();
	const signedInHint = useAuthHint();

	if (isAlwaysBare(pathname)) return <>{children}</>;

	// Auth-aware pages (e.g. /pricing): bare while loading to avoid a flash,
	// then shell only for authenticated users.
	if (AUTH_AWARE_EXACT.has(pathname)) {
		// Until the session answers, trust the server's cookie hint so the first paint is already the right one.
		const signedIn = status === "loading" ? signedInHint : Boolean(user);
		return signedIn ? <AppShell>{children}</AppShell> : <>{children}</>;
	}

	// Everything else is an authed management page → render the shell.
	return <AppShell>{children}</AppShell>;
}
